import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import PageHero from '../components/PageHero';
import CTA from '../components/CTA';
import services from '../data/services';
import './About.css';

const values = [
  { title: 'Excellence', description: 'We strive for excellence in everything we do, from code quality to client relationships. Our commitment to high standards ensures exceptional outcomes.' },
  { title: 'Innovation', description: "We stay at the forefront of technology, continuously learning and adapting to bring the latest innovations to our clients' projects." },
  { title: 'Integrity', description: 'Trust is the foundation of our business. We operate with transparency, honesty, and ethical practices in all our interactions.' },
  { title: 'Collaboration', description: 'We believe in the power of teamwork, both within our organization and with our clients, to achieve the best possible results.' },
];

const About = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    mainEntity: {
      '@type': 'Organization',
      name: 'ESCR LLC',
      description: 'ESCR LLC is a leading software development company dedicated to creating innovative solutions that help businesses thrive in the digital age.',
      url: 'https://escr.online/about',
      foundingDate: '2020',
      numberOfEmployees: {
        '@type': 'QuantitativeValue',
        value: '10-50'
      }
    }
  };

  return (
    <div className="about">
      <SEO
        title="About Us"
        description="ESCR LLC is a leading software development company dedicated to creating innovative solutions. Learn about our team, values, and services."
        keywords="about ESCR LLC, software development company, web development services, mobile app development, cloud solutions"
        structuredData={structuredData}
      />

      <PageHero
        eyebrow="About ESCR"
        title={
          <>
            Eat. Sleep. Code. <span className="text-gradient">Repeat.</span>
          </>
        }
        subtitle="Driving innovation through exceptional software development."
      />

      {/* Who we are */}
      <section className="section">
        <div className="container about-intro">
          <Reveal className="about-intro-head">
            <span className="eyebrow">Who we are</span>
            <h2 className="section-title">Engineers first. Partners always.</h2>
          </Reveal>
          <Reveal delay={100} className="about-intro-body">
            <p>
              ESCR LLC is a leading software development company dedicated to creating innovative solutions that help
              businesses thrive in the digital age. With years of experience and a passion for excellence, we've
              established ourselves as trusted partners for organizations seeking cutting-edge technology solutions.
            </p>
            <p>
              Our team of skilled developers, designers, and consultants work collaboratively to deliver software that
              not only meets but exceeds our clients' expectations. We believe in building long-term relationships with
              our clients, understanding their unique challenges, and providing tailored solutions that drive real
              results.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Manifesto code block */}
      <section className="section section-tight">
        <div className="container">
          <Reveal className="manifesto">
            <div className="manifesto-bar">
              <span className="manifesto-file">
                <Icon name="terminal" />
                values.json
              </span>
              <span className="manifesto-hint">{'// our core values'}</span>
            </div>
            <div className="values">
              {values.map((value, i) => (
                <div className="value" key={value.title}>
                  <span className="value-key">
                    <span className="value-index">0{i + 1}</span>"{value.title.toLowerCase()}"
                  </span>
                  <h3 className="value-title">{value.title}</h3>
                  <p className="value-desc">{value.description}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* What we do */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="eyebrow">What we do</span>
            <h2 className="section-title">Four disciplines, one team.</h2>
          </Reveal>
          <div className="about-services">
            {services.map((service, i) => (
              <Reveal key={service.slug} delay={i * 60}>
                <Link to={`/services/${service.slug}`} className="about-service">
                  <span className="about-service-num">0{i + 1}</span>
                  <span className="about-service-name">{service.name}</span>
                  <span className="about-service-desc">{service.intro.split('. ')[0]}.</span>
                  <span className="about-service-arrow">
                    <Icon name="arrowUpRight" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section mission">
        <div className="container">
          <Reveal className="mission-inner">
            <span className="eyebrow">Our mission</span>
            <p className="mission-text">
              To empower businesses with innovative software solutions that drive{' '}
              <span className="text-gradient">growth, efficiency, and competitive advantage.</span>{' '}
              <span className="text-muted">
                We are committed to delivering excellence, fostering innovation, and building lasting partnerships with
                our clients.
              </span>
            </p>
          </Reveal>
        </div>
      </section>

      <CTA />
    </div>
  );
};

export default About;
